/**
 * Reference Code Mapping for Foundations
 * Maps field names to Evaluate codes (resourceCode in Item Library API)
 */

export interface FoundationsReferenceCodeMapping {
  code?: string;
}

/**
 * Concrete-type → ready-mix concrete (RMC) resource-code map.
 * Used by the three "Concrete to ___" fields in the Foundations configure modal.
 *
 * Each concrete field combines its placement prefix (see CONCRETE_FIELD_PREFIX_CODES)
 * with the RMC code below to form a `<prefix>+<rmc>` resource-code string. Downstream
 * rate lookup splits on `+` and sums the placement and ready-mix nettRates.
 */
export const CONCRETE_STRIP_CODE_MAP: Record<string, string> = {
  GEN1: 'RMC0001',
  GEN2: 'RMC0002',
  GEN3: 'RMC0003',
  FND2: 'RMC0015',
  FND3z: 'RMC0016',
  FND4: 'RMC0017',
  'RC28/35': 'RMC0004',
  'RC32/40': 'RMC0014',
  'C16/20': 'RMC0013',
};

/**
 * Per-concrete-field placement prefix codes. Combined with the selected concrete
 * type's RMC code from CONCRETE_STRIP_CODE_MAP to produce the field's full
 * resource code (e.g. `GWALP013+RMC001` for `gen1ConcreteToStrip` with GEN1).
 *
 * `gen1AdditionalConcrete` (Abnormals tab) reuses the same `GWALP013`
 * placement prefix as `gen1ConcreteToStrip`.
 */
export const CONCRETE_FIELD_PREFIX_CODES: Record<string, string> = {
  gen1ConcreteToStrip: 'GWALP013',
  rc35ConcreteToPiled: 'GWALP019a',
  rc35ConcreteToVibro: 'GWALP025',
  gen1AdditionalConcrete: 'GWALP013',
};

/**
 * Foundations Reference Codes
 * Maps abbreviated descriptions from take-off sheets to Evaluate codes
 */
export const FOUNDATIONS_REFERENCES: Record<
  string,
  FoundationsReferenceCodeMapping
> = {
  // Excavation & Concrete
  pileMat: { code: "GWALP004" },
  pileMatRemoval: { code: "GWALP002" },
  plotGarSetOut: { code: "GWALP039" },
  pileSetOut: { code: "GWALP016" },
  pileAttendance: { code: "GWALP015" },
  excavateTrench: { code: "GWALP010" },
  excavatePiled: { code: "GWALP018" },
  excavateVibro: { code: "GWALP018" },
  levelCompact: { code: "GWALP012" },
  stockpileArisings: { code: "GWALP035" },
  concreteBlinding: { code: "GWALP013a" },
  formplasOrSimilar: { code: "GWALP020" },
  cropPiles: { code: "GWALP017" },
  fillSteelPiles: { code: "GWALP052" },
  rebar: { code: "GWALP024" },
  stepsInFounds: { code: "GWALP011" },
  meshReinforcement: {},
  gen1ConcreteToStrip: {}, // Dynamic — code set from CONCRETE_STRIP_CODE_MAP based on selected concrete type
  rc35ConcreteToPiled: {}, // Dynamic — code set from CONCRETE_STRIP_CODE_MAP based on selected concrete type
  rc35ConcreteToVibro: {}, // Dynamic — code set from CONCRETE_STRIP_CODE_MAP based on selected concrete type
  stripClaymaster: { code: "GWALP022" },
  piledClaymaster: { code: "GWALP022" },

  // Substructure - Trench Blocks
  trench4NBlock300mmWide: { code: "GWALPS003" },
  trench7NBlock300mmWide: { code: "GWALPS005" },
  trench4NBlock355mmWide: { code: "GWALPS004" },
  trench7NBlock355mmWide: { code: "GWALPS006" },

  // Substructure - Blockwork
  dense4NBlockwork100mm: { code: "GWALPS047" },
  dense7NBlockwork100mm: { code: "GWALPS019" },
  dense10NBlockwork100mm: { code: "GWALPS048" },
  aerated4NBlockwork100mm: { code: "GWALPS045" },
  aerated7NBlockwork100mm: { code: "GWALPS007" },
  aerated4NBlockwork140mm: { code: "GWALPS046" },
  aerated7NBlockwork140mm: { code: "GWALPS008" },

  // Substructure - Brickwork
  dense100mmCoursingBrick: { code: "GWALPS020" },
  aerated100mmCoursingBrick: { code: "GWALPS032" },
  aerated140mmCoursingBrick: { code: "GWALPS033" },
  layFacingBrickwork: { code: "GWALPS011" },
  supplyFacingBrickwork: { code: "GWALPS018" },
  beamInfill: { code: "GWALPS024" },
  formCavity: { code: "GWALPS013" },
  cavityFill: { code: "GWALPS014" },
  telescopicVents: { code: "GWALPS015" },
  telescopicVents140: { code: "GWALPS023" },
  perimeterInsulation: { code: "GWALPS026" },
  perimeterInsulation30: { code: "GWALPS027" },
  dpc100mm: { code: "GWALPS016" },
  backfillExcavations: { code: "GWALP034" },
  cavityInsulation: { code: "GWALPS025" },

  // Abnormals
  additionalExcavation: { code: "GWALP010" },
  gen1AdditionalConcrete: { code: "GWALP013" },
  additional300mmTrenchBlock: { code: "GWALPS005" },
  additional355mmTrenchBlock: { code: "GWALPS006" },
  additional100mmBlockwork: { code: "GWALPS007" },
  backfillExcavationsAsDug: { code: "GWALP034" },
  depositOnSite: { code: "GWALP035" },
  eoExposedBrickwork: { code: "GWALPS017" },
  eoTanking: { code: "GWALPS017A" },
  depositCFAArisings: { code: "ALP033a" },
  pileCollar: { code: "GWALP017a" },
  cordekCellcore155: { code: "GWALP021B" },
  cordekCellcore225: { code: "GWALP021" },

  // Floors
  bbFloorHouse: {}, // Dynamic — code set from prefixValues when floor type is selected
  bbFloorGarage: {}, // Dynamic — code set from prefixValues when garage floor type is selected
  layOnlyFloors: { code: "GWALPF031" },
  insituFloorHouse: { code: "GWALPF012" }, // Insitu Floor House (static)
  topHats: { code: "GWALPF013" },
  eo2000gDPM: { code: "CA0004" },
  eoAmber1DPM: { code: "ZEV24" },
  eoAmber2DPM: { code: "ZEV25" },
  radonMembrane: { code: "SCBB006" },
  gasMembrane: { code: "SCBB006" },
  meshToFloor: {},
  topLayerMesh: {},
  insituFloorGarage: { code: "GWALPF012" },
  meshToGarageFloor: {},
  raft: {},
};

/**
 * Mesh Reinforcement Type to Code Mapping
 * Maps selected mesh type to resource code (Evaluate codes)
 */
export const MESH_TYPE_TO_CODE: Record<string, string> = {
  'A142':  'GWALPRM001',
  'A193':  'GWALPRM002',
  'A252':  'GWALPRM003',
  'A393':  'GWALPRM004',
  'B283':  'GWALPRM017',
  'B385':  'GWALPRM005',
  'B503':  'GWALPRM006',
  'B785':  'GWALPRM007',
  'B1131': 'GWALPRM008',
};

/**
 * Celcon block type overrides for substructure fields.
 * Applied for both "Celcon - general" and "Celcon - Barratt / Redrow deal".
 */
export const CELCON_GENERAL_CODE_OVERRIDES: Record<string, string> = {
  trench4NBlock300mmWide: 'GWALPS028',
  trench7NBlock300mmWide: 'GWALPS030',
  trench4NBlock355mmWide: 'GWALPS029',
  trench7NBlock355mmWide: 'GWALPS031',
  aerated7NBlockwork100mm: 'GWALPS037',
  aerated7NBlockwork140mm: 'GWALPS060',
  aerated100mmCoursingBrick: 'GWALPS038',
};

export const CELCON_BLOCK_TYPES = ['Celcon - general', 'Celcon - Barratt / Redrow deal'] as const;

/** Returns a merged references map with block-type-specific codes applied. */
export const applyBlockTypeOverrides = (
  refs: Record<string, FoundationsReferenceCodeMapping>,
  blockType?: string,
): Record<string, FoundationsReferenceCodeMapping> => {
  if (!blockType || !(CELCON_BLOCK_TYPES as readonly string[]).includes(blockType)) return refs;
  const result = { ...refs };
  Object.entries(CELCON_GENERAL_CODE_OVERRIDES).forEach(([field, code]) => {
    result[field] = { code };
  });
  return result;
};

/**
 * Substructure Block Type to Code Suffix Mapping
 */
export const BLOCK_TYPE_TO_SUFFIX: Record<string, string> = {
  'Dense': 'DENSE',
  'Aerated': 'AERATED',
  'Thermalite': 'THERM',
  // Add more block types as needed
};

/**
 * Get reference code for a specific field
 */
export const getFoundationsReferenceCode = (
  referenceKey: string,
): string | undefined => {
  return FOUNDATIONS_REFERENCES[referenceKey]?.code;
};

/**
 * Get full reference information for a specific field
 */
export const getFoundationsReference = (
  referenceKey: string,
): FoundationsReferenceCodeMapping | undefined => {
  return FOUNDATIONS_REFERENCES[referenceKey];
};

/**
 * Get mesh reinforcement code from type selection
 */
export const getMeshReinforcementCode = (
  meshType: string | undefined,
): string | undefined => {
  if (!meshType) {
    return undefined;
  }

  // Try exact match first
  if (MESH_TYPE_TO_CODE[meshType]) {
    return MESH_TYPE_TO_CODE[meshType];
  }

  // Try to extract mesh code from string (e.g., "A393 Mesh" -> "A393")
  const match = meshType.match(/([AB]\d+)/i);
  if (match && MESH_TYPE_TO_CODE[match[1].toUpperCase()]) {
    return MESH_TYPE_TO_CODE[match[1].toUpperCase()];
  }

  return undefined;
};
